import React, { useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";

export default function ClientAccount() {
  const { profile, session } = useAuth();

  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  const [prefs, setPrefs] = useState(
    profile?.notification_preferences || { invoice_uploaded: true, report_ready: true }
  );
  const [savingPrefs, setSavingPrefs] = useState(false);

  async function saveProfile() {
    setSavingProfile(true);
    setProfileMessage(null);
    const { error } = await supabase.from("users").update({ full_name: fullName }).eq("id", profile.id);
    setSavingProfile(false);
    setProfileMessage(error ? error.message : "Saved.");
  }

  async function savePassword() {
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }
    setSavingPassword(true);
    setPasswordError(null);
    setPasswordMessage(null);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSavingPassword(false);
    if (error) {
      setPasswordError(error.message);
      return;
    }
    setNewPassword("");
    setPasswordMessage("Password updated.");
  }

  async function togglePref(key) {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSavingPrefs(true);
    await supabase.from("users").update({ notification_preferences: updated }).eq("id", profile.id);
    setSavingPrefs(false);
  }

  return (
    <div className="max-w-xl">
      <h1 className="text-xl font-semibold text-foreground mb-5">Account</h1>

      <div className="border border-border rounded-2xl bg-card p-5 mb-5">
        <div className="text-sm font-semibold text-foreground mb-1">Profile</div>
        <p className="text-xs text-muted-foreground mb-4">Single login for your company</p>

        <div className="mb-3">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background text-foreground"
          />
        </div>
        <div className="mb-3">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
          <input
            value={session?.user?.email || ""}
            disabled
            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-accent text-muted-foreground"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Email is your login, contact your account executive to change it.
          </p>
        </div>
        {profileMessage && <p className="text-xs text-muted-foreground mb-2">{profileMessage}</p>}
        <button
          onClick={saveProfile}
          disabled={savingProfile}
          className="border border-input rounded-lg px-4 py-2 text-sm disabled:opacity-60"
        >
          {savingProfile ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="border border-border rounded-2xl bg-card p-5 mb-5">
        <div className="text-sm font-semibold text-foreground mb-3">Password</div>
        <input
          type="password"
          placeholder="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background text-foreground mb-2"
        />
        {passwordError && <p className="text-xs text-destructive mb-2">{passwordError}</p>}
        {passwordMessage && <p className="text-xs text-muted-foreground mb-2">{passwordMessage}</p>}
        <button
          onClick={savePassword}
          disabled={savingPassword}
          className="border border-input rounded-lg px-4 py-2 text-sm disabled:opacity-60"
        >
          {savingPassword ? "Updating..." : "Change password"}
        </button>
      </div>

      <div className="border border-border rounded-2xl bg-card p-5">
        <div className="text-sm font-semibold text-foreground mb-1">Notifications</div>
        <p className="text-xs text-muted-foreground mb-4">
          Preference only for now, email sending itself is a later addition
        </p>
        <ToggleRow
          label="New invoice uploaded"
          sub="Notify when a new invoice is ready"
          checked={prefs.invoice_uploaded}
          onChange={() => togglePref("invoice_uploaded")}
          disabled={savingPrefs}
        />
        <ToggleRow
          label="Monthly report ready"
          sub="Notify when this month's numbers are updated"
          checked={prefs.report_ready}
          onChange={() => togglePref("report_ready")}
          disabled={savingPrefs}
        />
      </div>
    </div>
  );
}

function ToggleRow({ label, sub, checked, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <div>
        <div className="text-sm">{label}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="w-4 h-4" />
    </div>
  );
}
